/**
 * Pure multi-card selection helpers for the game table hand.
 * Supports pick-1 (toggle/replace), pick-2, and pick-3 ordered selection.
 */

/** Toggle a card in/out of the ordered selection, respecting `pick`. */
export function toggleCardSelection(
  selectedIds: readonly string[],
  cardId: string,
  pick: number,
): string[] {
  const current = [...selectedIds];
  const index = current.indexOf(cardId);

  if (index >= 0) {
    current.splice(index, 1);
    return current;
  }

  if (pick <= 1) {
    return [cardId];
  }

  if (current.length >= pick) {
    return current;
  }

  return [...current, cardId];
}

export function clearCardSelection(): string[] {
  return [];
}

export function selectionOrder(selectedIds: readonly string[], cardId: string): number | null {
  const index = selectedIds.indexOf(cardId);
  return index >= 0 ? index + 1 : null;
}

export function isSelectionComplete(selectedIds: readonly string[], pick: number): boolean {
  return selectedIds.length === pick;
}

export function hasDuplicateSelection(selectedIds: readonly string[]): boolean {
  return new Set(selectedIds).size !== selectedIds.length;
}

export interface HandCardLike {
  id: string;
  type?: 'white' | 'wild';
}

/** Build ordered write-in texts for wild cards in the selection (one entry per wild, in order). */
export function writeInsForSelection(
  selectedIds: readonly string[],
  hand: readonly HandCardLike[],
  writeInText: string,
): string[] {
  const trimmed = writeInText.trim();
  if (!trimmed) return [];
  return selectedIds
    .map((id) => hand.find((c) => c.id === id))
    .filter((c) => c?.type === 'wild')
    .map(() => trimmed);
}

export function wildCardsInSelection(
  selectedIds: readonly string[],
  hand: readonly HandCardLike[],
): HandCardLike[] {
  return selectedIds
    .map((id) => hand.find((c) => c.id === id))
    .filter((c): c is HandCardLike => Boolean(c?.type === 'wild'));
}

export function submitButtonLabel(selectedCount: number, pick: number): string {
  if (pick <= 1) {
    return selectedCount === 1 ? 'Submit Card' : 'Choose a card';
  }
  if (selectedCount === 0) {
    return `Choose ${pick} cards`;
  }
  if (selectedCount < pick) {
    return `${selectedCount} / ${pick} selected`;
  }
  return `Submit ${pick} cards`;
}

export function submitDisabledReason(input: {
  pick: number;
  selectedCount: number;
  maySubmit: boolean;
  viewerHasSubmitted: boolean;
  isJudge: boolean;
}): string | null {
  if (input.isJudge) return 'You are the judge this round.';
  if (input.viewerHasSubmitted) return 'You already submitted this round.';
  if (!input.maySubmit) return 'Submissions are closed.';
  if (input.selectedCount < input.pick) {
    return input.pick === 1 ? 'Select a card to submit.' : `Select ${input.pick} cards to submit.`;
  }
  return null;
}

/** Map engine/server error codes to short user-facing titles. */
export function formatServerError(raw: string): { title: string; detail: string } {
  const match = raw.match(/^([A-Z_]+):\s*(.+)$/);
  if (!match) return { title: 'Something went wrong', detail: raw };

  const code = match[1]!;
  const message = match[2]!;
  const titles: Record<string, string> = {
    PLAYER_KICKED: 'Removed from room',
    ROOM_NOT_FOUND: 'Room not found',
    ROOM_FULL: 'Room is full',
    ROOM_NOT_JOINABLE: 'Game already started',
    NOT_HOST: 'Host only',
    JUDGE_CANNOT_SUBMIT: 'Judge cannot submit',
    NOT_JUDGE: 'Judge only',
    WRONG_CARD_COUNT: 'Wrong number of cards',
    CARD_NOT_IN_HAND: 'Card not in your hand',
    WRITE_IN_DISABLED: 'Write-ins disabled',
    WRITE_IN_INVALID: 'Invalid write-in',
    ALREADY_SUBMITTED: 'Already submitted',
    INVALID_PHASE: 'Not allowed right now',
    GAME_OVER: 'Game is over',
  };

  return {
    title: titles[code] ?? 'Server error',
    detail: message,
  };
}
